<template>
  <div class="frames-view-container">
    <div
      v-if="activeFrames.length === 0"
      class="frames-empty-state">
      <v-icon
        size="48"
        color="grey">
        mdi-monitor-off
      </v-icon>
      <div class="mt-2">No frames configured</div>
    </div>
    <div
      v-for="frame in activeFrames"
      :key="frame.id"
      class="frame-column">
      <div
        class="frame-header"
        :style="frameHeaderStyle(frame)">
        <v-icon
          size="small"
          class="mr-2">
          {{ `mdi-numeric-${frame.number}-box` }}
        </v-icon>
        <span class="frame-header-name">
          {{ frame.nickname || `Frame ${frame.number}` }}
        </span>
      </div>
      <div class="frame-queues">
        <template v-if="getChannelQueues(frame)">
          <div
            v-for="queue in getChannelQueues(frame)"
            :key="queue.did"
            class="frame-queue-section">
            <template v-if="queue.did && deluge.items[queue.did]">
              <div
                class="queue-header"
                :class="{ 'queue-live': queue.live }">
                <v-icon
                  size="small"
                  class="mr-2">
                  mdi-playlist-play
                </v-icon>
                <span class="queue-header-name">
                  {{ deluge.items[queue.did].name }}
                </span>
              </div>
              <div
                v-if="queue.items"
                class="queue-items">
                <template
                  v-for="(item, idx) in queue.items"
                  :key="item?.did || idx">
                  <div
                    v-if="item?.did && deluge.items[item.did]"
                    class="scene-row"
                    :class="{
                      'scene-live': item.live,
                      'scene-current': idx === queue.index && !item.live,
                    }"
                    @click="forceLive(item.did, frame)"
                    :title="
                      item.live
                        ? 'Currently live'
                        : `Force live: ${deluge.items[item.did].name}`
                    ">
                    <v-icon
                      size="small"
                      class="scene-icon mr-2">
                      {{ getTemplateIcon(deluge.items[item.did]) }}
                    </v-icon>
                    <span class="scene-name">
                      {{ deluge.items[item.did].name }}
                    </span>
                    <v-progress-circular
                      v-if="loadingDid === item.did"
                      indeterminate
                      color="white"
                      :size="18"
                      :width="2"
                      class="ml-auto" />
                    <v-icon
                      v-else-if="item.live"
                      size="small"
                      class="ml-auto live-icon">
                      mdi-broadcast
                    </v-icon>
                    <v-icon
                      v-else
                      size="small"
                      class="ml-auto scene-go-icon">
                      mdi-chevron-double-right
                    </v-icon>
                  </div>
                </template>
              </div>
              <!-- Nested queues (flattened one level) -->
              <template v-if="queue.items">
                <template
                  v-for="item in queue.items"
                  :key="'nested-' + (item?.did || '')">
                  <div
                    v-if="item?.type === 'queue' && item.did && deluge.items[item.did] && item.items"
                    class="nested-queue-section">
                    <div
                      class="queue-header nested"
                      :class="{ 'queue-live': item.live }">
                      <v-icon
                        size="small"
                        class="mr-2">
                        mdi-subdirectory-arrow-right
                      </v-icon>
                      <span class="queue-header-name">
                        {{ deluge.items[item.did].name }}
                      </span>
                    </div>
                    <div class="queue-items nested">
                      <template
                        v-for="(subItem, subIdx) in item.items"
                        :key="subItem?.did || subIdx">
                        <div
                          v-if="subItem?.did && deluge.items[subItem.did]"
                          class="scene-row"
                          :class="{
                            'scene-live': subItem.live,
                            'scene-current': subIdx === item.index && !subItem.live,
                          }"
                          @click="forceLive(subItem.did, frame)"
                          :title="
                            subItem.live
                              ? 'Currently live'
                              : `Force live: ${deluge.items[subItem.did].name}`
                          ">
                          <v-icon
                            size="small"
                            class="scene-icon mr-2">
                            {{ getTemplateIcon(deluge.items[subItem.did]) }}
                          </v-icon>
                          <span class="scene-name">
                            {{ deluge.items[subItem.did].name }}
                          </span>
                          <v-progress-circular
                            v-if="loadingDid === subItem.did"
                            indeterminate
                            color="white"
                            :size="18"
                            :width="2"
                            class="ml-auto" />
                          <v-icon
                            v-else-if="subItem.live"
                            size="small"
                            class="ml-auto live-icon">
                            mdi-broadcast
                          </v-icon>
                          <v-icon
                            v-else
                            size="small"
                            class="ml-auto scene-go-icon">
                            mdi-chevron-double-right
                          </v-icon>
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
              </template>
            </template>
          </div>
        </template>
        <div
          v-else
          class="frame-no-queues">
          No queues
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue';
import { deluge } from '@/stores/deluge.js';
import { $http } from '@/stores/http.js';

const loadingDid = ref(null);

const activeFrames = computed(() => {
  if (!deluge.frames) return [];
  return Object.values(deluge.frames)
    .filter((frame) => frame.did && deluge.channels?.[frame.did])
    .sort((a, b) => a.id - b.id);
});

function getChannelQueues(frame) {
  const channel = deluge.channels?.[frame.did];
  return channel?.queues || null;
}

function getTemplateIcon(item) {
  if (!item?.scene_name) return 'mdi-cube';
  const template = deluge.templates?.[item.scene_name];
  return template?.icon || 'mdi-cube';
}

function frameHeaderStyle(frame) {
  if (frame.color) {
    return `border-top: 4px solid ${frame.color}, 1);`;
  }
  return '';
}

async function forceLive(did, frame) {
  if (!did || loadingDid.value) return;
  loadingDid.value = did;
  try {
    let url = '/dynamic_engine/go_live?did=' + did;
    if (frame?.did) url += '&channel_did=' + frame.did;
    await $http('GET', url);
  } catch (error) {
    console.error('Failed to force live:', error);
  }
  loadingDid.value = null;
}
</script>
<style scoped>
.frames-view-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 4px;
  flex-grow: 1;
  align-items: stretch;
}

.frames-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #888;
  font-size: 16px;
}

.frame-column {
  min-width: 280px;
  max-width: 280px;
  background: var(--v-backgroundColor-base);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.frame-header {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  font-weight: 600;
  font-size: 15px;
  color: white;
  background: var(--v-cardColor-base);
  user-select: none;
}

.frame-header-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.frame-queues {
  flex-grow: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.frame-queue-section {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.queue-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #ccc;
  background: var(--v-cardColor-base);
  border-radius: 6px;
  user-select: none;
}

.queue-header.nested {
  margin-left: 14px;
  font-size: 13px;
  padding: 8px 10px;
}

.queue-header.queue-live {
  color: white;
  background: rgb(var(--v-theme-primary));
}

.queue-header-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.queue-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.queue-items.nested {
  margin-left: 14px;
}

.nested-queue-section {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 6px;
}

.scene-row {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 14px;
  color: #bbb;
  background: var(--v-cardColor-base);
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition:
    background 0.15s ease,
    transform 0.1s ease;
}

.scene-row:hover {
  background: rgba(255, 255, 255, 0.08);
  color: white;
}

.scene-row:active {
  transform: scale(0.97);
}

.scene-row.scene-live {
  background: rgb(var(--v-theme-primary));
  color: white;
}

.scene-row.scene-current {
  border-left: 3px solid rgb(var(--v-theme-primary));
}

.scene-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.live-icon {
  color: white;
}

.scene-go-icon {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.scene-row:hover .scene-go-icon {
  opacity: 0.7;
}

.frame-no-queues {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  padding: 24px;
}
</style>
